import MessageChatCircleIcon from "@redotech/redo-icons/message-chat-circle.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useEffect, useState } from "react";
import {
  EscalationStatus as Status,
  useEscalationContext,
} from "./escalation-context";

export interface EscalationStatusProps {
  agentName?: string | null;
  agentAvatarUrl?: string | null;
}

export const EscalationStatus = memo(function EscalationStatus({
  agentName,
  agentAvatarUrl,
}: EscalationStatusProps) {
  const { escalation } = useEscalationContext();

  if (escalation.status === "idle") {
    return null;
  }

  return (
    <Flex align="center" gap="xs" justify="center" py="xs">
      <StatusIndicator
        agentAvatarUrl={agentAvatarUrl}
        agentName={agentName}
        status={escalation.status}
      />
    </Flex>
  );
});

// Animated dots component: cycles through "", ".", "..", "..."
const AnimatedDots = memo(function AnimatedDots() {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <span style={{ width: "1.5em", display: "inline-block" }}>
      {".".repeat(dotCount)}
    </span>
  );
});

interface StatusIndicatorProps {
  status: Status;
  agentName?: string | null;
  agentAvatarUrl?: string | null;
}

const CONNECTED_DISPLAY_DURATION_MS = 15_000;
const FADE_DURATION_MS = 500;
const WAITING_MESSAGE_SWITCH_MS = 2500;

const StatusIndicator = memo(function StatusIndicator({
  status,
  agentName,
  agentAvatarUrl,
}: StatusIndicatorProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isFading, setIsFading] = useState(false);
  const [waitingPhase, setWaitingPhase] = useState<"connecting" | "waiting">(
    "connecting",
  );

  // Switch waiting message after 5 seconds
  useEffect(() => {
    if (status === "waiting") {
      setWaitingPhase("connecting");
      const timer = setTimeout(() => {
        setWaitingPhase("waiting");
      }, WAITING_MESSAGE_SWITCH_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [status]);

  // Fade out then hide the "connected" message after 15 seconds
  useEffect(() => {
    if (status === "human-active") {
      setIsVisible(true);
      setIsFading(false);

      const fadeTimer = setTimeout(() => {
        setIsFading(true);
      }, CONNECTED_DISPLAY_DURATION_MS);

      const hideTimer = setTimeout(() => {
        setIsVisible(false);
      }, CONNECTED_DISPLAY_DURATION_MS + FADE_DURATION_MS);

      return () => {
        clearTimeout(fadeTimer);
        clearTimeout(hideTimer);
      };
    }
    return undefined;
  }, [status, agentName]);

  switch (status) {
    case "waiting":
      return (
        <>
          {waitingPhase === "connecting" && <RedoSpinner size="xs" />}
          <Text color="secondary" fontSize="xs">
            {waitingPhase === "connecting"
              ? "Connecting you with support"
              : "Waiting for team member to join"}
            <AnimatedDots />
          </Text>
        </>
      );

    case "human-active": {
      // Keep showing waiting state until we have the agent's name
      if (!agentName) {
        return (
          <Text color="secondary" fontSize="xs">
            Waiting for team member to join
            <AnimatedDots />
          </Text>
        );
      }
      if (!isVisible) {
        return null;
      }
      const displayAvatarUrl = agentAvatarUrl?.trim();
      return (
        <Flex
          align="center"
          gap="xs"
          style={{
            opacity: isFading ? 0 : 1,
            transition: `opacity ${FADE_DURATION_MS}ms ease-out`,
          }}
        >
          {displayAvatarUrl ? (
            <img
              alt={agentName}
              referrerPolicy="no-referrer"
              src={displayAvatarUrl}
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
          ) : (
            <RedoIcon color="success" Icon={MessageChatCircleIcon} size={16} />
          )}
          <Text color="primary" fontSize="xs" fontWeight="medium">
            Connected with {agentName}
          </Text>
        </Flex>
      );
    }

    case "closed":
      return null;

    default:
      return null;
  }
});
